import { useEffect, useMemo, useRef, useState } from 'react';
import { Subject, of, BehaviorSubject } from 'rxjs';
import { catchError, concatMap, finalize, first, map, filter } from 'rxjs/operators';
import {
    IDataPackage,
    IDataPackageApi,
    IDataPackageConfigProps,
    IDataPackageFileDescriptor
} from '@vivli/shared/features/data-package/infrastructure/interface';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { downloadFile as downloadFileToBrowser, useErrorHandling } from '@vivli/shared/infrastructure/hook';
import moment from 'moment';
import sanitize from 'sanitize-filename';
import { v4 as uuidv4 } from 'uuid';
import { IFileExtension } from '@vivli/shared/infrastructure/interface';
import {
    useActiveUserContext,
    useDataPackageApiContext,
    useDataPackageFileApiContext,
    useModalApiContext,
    useOrganizationsApiContext,
    useStudiesApiContext,
    useToastApiContext,
    useVivliConfigContext
} from '@vivli/core/infrastructure/context';
import { DataPackageValidationModeEnum } from '@vivli/features/studies/infrastructure/enum';

export interface IDataPackageInternalApi {
    onPubliclyAvailableFilesChanged: (files: IDataPackageFileDescriptor[]) => void;
}

interface UseDataPackageProps extends IDataPackageConfigProps {
    dataPackageId: string;
    secondaryDataPackageId?: string;
    publiclyAvailableOrgId?: string;
    readOnly?: boolean;
    onDataPackageReady?: (dataPackage: IDataPackage) => void;
}

export interface IDataPackageHookApi {
    dataPackage: IDataPackage | undefined;
    files: IDataPackageFileDescriptor[] | undefined;
    publiclyAvailableOrg: IOrganization | undefined;
    isLoading: boolean;
    fileLimitReached: boolean;
    hasRequiredTypes: boolean;
    getDataPackage: () => void;
    setDataPackage: (dataPackage: IDataPackage) => void;
    validateUploadedData: () => void;
    setAdditionalFileProps: (additionalFileProps: any) => void;

    onPreExistingFile?: (name: string) => Promise<boolean>;
    onFileDeleted: (file: IDataPackageFileDescriptor) => Promise<void>;
    onFileDownload?: (file: IDataPackageFileDescriptor) => void;
    onFileTypeChanged?: (file: IDataPackageFileDescriptor, newFileType: string) => void;
    onFileUploadCompleted?: (id: string, name: string) => void;
    onFileUploadError?: (id: string, name: string, error: string) => void;
    onFileUploadStart?: (id: string, fileName: string, fileSizeKb: number) => void;
    onFileUploadProgress?: (id: string, name: string, percentUploaded: number) => void;
    onAllFilesCompleted?: (succeededFileIds: string[], failedFileIds: string[]) => void;
    onFileValidation?: (name: string) => boolean | Promise<boolean>;
    onPubliclyAvailableChanged?: (file: IDataPackageFileDescriptor, isPubliclyAvailable: boolean) => void;
}

export const useDataPackage = ({
    dataPackageId,
    secondaryDataPackageId,
    onSecondaryDataPackageUpdated,
    secondaryFilesToShow,
    publiclyAvailableOrgId,
    readOnly,
    requiredTypes,
    validationMode,
    showDeleteDialog,
    onDataPackageReady,
    onValidation,
    onFileAdded,
    onFileRemoved,
    recordFileDownload,
    datasetMetaDataDoi,
    studyOrgId,
    showPubliclyAvailable,
    publicAccessByDefaultFileTypes,
    sortFilesByDate,
    fileLimit,
    fileExtensions,
    onFileExtensionWarning,
    showFileWarning,
    onPackageUpdated,
    overrideDataPackage
}: UseDataPackageProps): IDataPackageHookApi => {
    const initializedRef = useRef(false);

    const _dataPackageService = useDataPackageApiContext();
    const _dataPackageFileService = useDataPackageFileApiContext();
    const _modalService = useModalApiContext();
    const _toastService = useToastApiContext();
    const { handleError } = useErrorHandling();
    const _orgsService = useOrganizationsApiContext();
    const _studiesService = useStudiesApiContext();
    const { user, orgHasPermission } = useActiveUserContext();
    const config = useVivliConfigContext();

    const dataPackageRef = useRef<IDataPackage>(null);
    const fileExtensionsRef = useRef<IFileExtension[]>([]);
    const additionalFilePropsRef = useRef<any>({});
    const fileUpdateQueueRef = useRef<Subject<{ updatedFile: IDataPackageFileDescriptor; errorMessage: string }>>(new Subject());

    const [dataPackage, setDataPackageState] = useState<IDataPackage>();
    const [publiclyAvailableOrg, setPubliclyAvailableOrg] = useState<IOrganization>();
    const [isLoading, setIsLoadingState] = useState(false);
    const isLoading$ = useMemo(() => new BehaviorSubject(false), []);
    const setIsLoading = (loading: boolean) => {
        isLoading$.next(loading);
        setIsLoadingState(loading);
    };

    useEffect(() => {
        const subscription = fileUpdateQueueRef.current
            .pipe(
                concatMap(({ updatedFile, errorMessage }) => {
                    return isLoading$.pipe(
                        filter((loading) => !loading),
                        first(),
                        concatMap(() => {
                            setIsLoading(true);
                            return _dataPackageService.addUpdateFile(dataPackageId, updatedFile).pipe(
                                first(),
                                map((dp) => ({ dp, updatedFile })),
                                catchError((err) => {
                                    _handleError(errorMessage, err);
                                    return of({ dp: null, updatedFile });
                                }),
                                finalize(() => setIsLoading(false))
                            );
                        })
                    );
                })
            )
            .subscribe(({ dp, updatedFile }: any) => {
                if (dp) {
                    setDataPackage(dp);
                    _updateFile(updatedFile);
                }
            });

        return () => subscription.unsubscribe();
    }, [dataPackageId, _dataPackageService, isLoading$]);
    const [hasRequiredTypes, setHasRequiredTypes] = useState<boolean>(false);

    const filesRef = useRef<IDataPackageFileDescriptor[]>();
    const filesRefCount = useRef(0);
    const [files, setFiles] = useState<IDataPackageFileDescriptor[]>();
    const [fileLimitReached, setFileLimitReached] = useState<boolean>(false);

    const _setFiles = (updatedFiles: IDataPackageFileDescriptor[]) => {
        // update the files ref state (needed for quick async uploads of multiple files
        filesRef.current = updatedFiles;

        // update the file limit if we have reached it
        _updateFileLimitReached();

        // set the files state to update screens
        setFiles(updatedFiles);
    };

    const _updateFile = (file: IDataPackageFileDescriptor) => {
        const updatedFiles = filesRef.current?.map((f) => (f.name === file.name ? file : f));
        _setFiles(updatedFiles);
    };

    const setDataPackage = (dataPackage: IDataPackage): void => {
        dataPackageRef.current = dataPackage;
        setDataPackageState(dataPackage);
        onPackageUpdated && onPackageUpdated(dataPackage);
    };

    const _handleError = (customMessage: string, error) => {
        handleError(customMessage, error);
        setIsLoading(false);
    };

    const _getPubliclyAvailableOrg = () => {
        if (publiclyAvailableOrgId !== 'FakeOrgId') {
            _orgsService
                .getOrganization(publiclyAvailableOrgId)
                .pipe(first())
                .subscribe({
                    next: setPubliclyAvailableOrg,
                    error: (error) => _handleError('Error getting organization.', error)
                });
        }
    };

    const _validateRequiredTypes = () => {
        if (!requiredTypes) {
            setHasRequiredTypes(true);
            return;
        }

        let _hasRequiredTypes = false;
        switch (validationMode) {
            case DataPackageValidationModeEnum.All:
                _hasRequiredTypes = requiredTypes.every((type) =>
                    dataPackage?.dataPackageFileDescriptors?.some((file) => file.type === type)
                );
                break;
            case DataPackageValidationModeEnum.Any:
                _hasRequiredTypes = requiredTypes.some((type) =>
                    dataPackage?.dataPackageFileDescriptors?.some((file) => file.type === type)
                );
                break;
        }

        setHasRequiredTypes(_hasRequiredTypes);
        onValidation && onValidation(_hasRequiredTypes);
    };

    const _handleFileDeleteWarning = (file: IDataPackageFileDescriptor) => {
        const isUploadedByCurrentUser = file.uploadedByUserId === user.userId;
        const modalMessage = isUploadedByCurrentUser
            ? `Are you sure you want to delete this file:  ${file.name}`
            : `This file was added by another user - are you sure you want to delete this file: ${file.name}`;

        return new Promise<void>((resolve, reject) => {
            _modalService.confirm(modalMessage, {
                confirmText: 'Ok',
                cancelText: 'Cancel',
                onConfirm: () => resolve(),
                onCancel: () => setIsLoading(false)
            });
        });
    };

    const _canUserSendDataCount = (studyOrgId: string) => {
        // when data contributor downloads its own data , it doesn't count
        //TODO PERMFE - redo with permissions+
        if (studyOrgId) {
            const orgs = user.orgMemberships?.filter((org) => {
                orgHasPermission(studyOrgId, PermissionsEnum.DataContrib);
            });
            return !(orgs && orgs.length > 0);
        }
        return false;
    };

    const _handleFileTypeChangeForPubliclyAvailable = (type: string, file: IDataPackageFileDescriptor, fromUser?: boolean) => {
        if (showPubliclyAvailable) {
            if (type === 'Unknown') {
                file.publicAccessDisabled = true;
                file.publicAccessGranted = false;
            }

            if (config?.ipdFileTypes) {
                //clear and disable checkbox if not shareable
                file.publicAccessDisabled = !config.ipdFileTypes.find((t) => t.fileType === type)?.publicAccessAllowable;
                if (file.publicAccessDisabled) {
                    file.publicAccessGranted = false;
                }
            }

            //if file type is shared by default by organization, set it to true - only on user's action
            if (fromUser && !file.publicAccessDisabled && publicAccessByDefaultFileTypes?.length > 0 && !file.publicAccessGranted) {
                file.publicAccessGranted = !!publicAccessByDefaultFileTypes.find((t) => t === type);
            }
        }
    };

    const onPreExistingFile = (name: string) => {
        return new Promise<boolean>((resolve, reject) => {
            _modalService.confirm(`A file named ${name} already exists.  Click OK to overwrite.`, {
                confirmText: 'OK',
                cancelText: 'Cancel',
                onConfirm: () => resolve(true),
                onCancel: () => reject(false)
            });
        });
    };

    const _downloadFile = (file: IDataPackageFileDescriptor) => {
        setIsLoading(true);
        _toastService.info('Preparing file for download...');
        //send data count

        if (datasetMetaDataDoi && datasetMetaDataDoi != 'DOINotCreated' && _canUserSendDataCount(studyOrgId)) {
            const fileIdentity = file.datapackageDoiFileNumber || file.name;
            _studiesService.sendDataCountCollectionData(datasetMetaDataDoi, fileIdentity).subscribe(
                (sent) => {
                    LoggerService.log(`Download count is ${sent}`);
                },
                (error) => {
                    LoggerService.log(`Download count is errored:  ${error}`);
                }
            );
        }

        const dataPackageIdToUse = secondaryDataPackageId && file.publicAccessGranted ? secondaryDataPackageId : dataPackageId;
        _dataPackageFileService
            .downloadFileUri(dataPackageIdToUse, file.name, recordFileDownload || false, true)
            .pipe(first())
            .subscribe(
                (downloadLink) => {
                    downloadFileToBrowser(downloadLink, file.name);
                    setIsLoading(false);
                },
                (error) => {
                    setIsLoading(false);
                    _handleError('Unable to download file.', error);
                }
            );
    };
    const _removeFromPublicAccess = (activeFile: IDataPackageFileDescriptor) => {
        //remove from Public access
        const updatedFile = {
            ...activeFile,
            publicAccessGranted: false,
            publicAccessDisabled: true
        };
        return _dataPackageService.addUpdateFile(secondaryDataPackageId, updatedFile).pipe(
            first(),
            map((dp) => {
                onSecondaryDataPackageUpdated && onSecondaryDataPackageUpdated(dp);
            }),
            finalize(() => {
                setIsLoading(false);
            })
        );
    };

    const _deleteFile = (file: IDataPackageFileDescriptor) => {
        setIsLoading(true);

        const isSecondaryFile = secondaryFilesToShow?.find((f) => f.name === file.name);
        if (isSecondaryFile) {
            return new Promise<void>((resolve) => {
                _removeFromPublicAccess(file).subscribe({
                    next: () => resolve(),
                    error: (err) => _handleError('Error deleting file.', err)
                });
            });
        }

        const observable = _dataPackageFileService.deleteFile(dataPackageId, file.name, true).pipe(
            first(),
            map((dataPackage) => {
                // decrement the file count
                filesRefCount.current -= 1;

                setDataPackage(dataPackage);
                _setFiles(filesRef.current.filter((f) => f.name !== file.name));

                // fire the file deleted event to bubble it up if needed
                onFileRemoved && onFileRemoved(file);
                return file;
            }),
            finalize(() => {
                setIsLoading(false);
            })
        );

        return new Promise<void>((resolve) => {
            if (showDeleteDialog) {
                _handleFileDeleteWarning(file).then(() => {
                    observable.subscribe({
                        next: () => resolve(),
                        error: (err) => _handleError('Error deleting file.', err)
                    });
                });
            } else {
                observable.subscribe({
                    next: () => resolve(),
                    error: (err) => _handleError('Error deleting file.', err)
                });
            }
        });
    };

    function _serverFileUpdate(updatedFile: IDataPackageFileDescriptor, errorMessage: string) {
        fileUpdateQueueRef.current.next({ updatedFile, errorMessage });
    }

    const _changeFileType = (file: IDataPackageFileDescriptor, type: string) => {
        const updatedFile = {
            ...file,
            type
        };

        _handleFileTypeChangeForPubliclyAvailable(type, updatedFile, true);

        _serverFileUpdate(updatedFile, `Unable to change file type for ${updatedFile.name}.`);
    };

    const _updateFileLimitReached = () => {
        if (fileLimit) {
            const limitReached = filesRefCount.current >= fileLimit;

            // if limit reached has changed, update the state
            if (limitReached !== fileLimitReached) {
                // notify we have reached the max number of files
                setFileLimitReached(limitReached);
            }
        }
    };

    const setAdditionalFileProps = (additionalFileProps: any) => {
        additionalFilePropsRef.current = additionalFileProps;
    };

    const validateUploadedData = () => {
        _dataPackageService
            .getDataPackageFileStatus(dataPackageId)
            .pipe(map((result) => result))
            .subscribe({
                next: () => {
                    _toastService.info('All data has been successfully uploaded and stored in the system.', {
                        autoClose: 9000
                    });
                },
                error: (err) =>
                    _modalService.error(
                        `The following files were not successfully uploaded - please notify Vivli at support@vivli.org, and include the study ID and if appropriate, the request number. Message: ${err}`
                    )
            });
    };

    const handlePubliclyAvailableChanged = (file: IDataPackageFileDescriptor, publicAccessGranted: boolean, localChange = false) => {
        const updatedFile = {
            ...file,
            publicAccessGranted
        };

        // only fire server update if this is not a local file change only
        if (localChange) return;

        _serverFileUpdate(updatedFile, `Unable to change Public Availability for ${updatedFile.name ?? 'N/A'}.`);
    };

    const onFileDeleted = (file: IDataPackageFileDescriptor) => {
        return _deleteFile(file);
    };

    const onFileDownload = (file: IDataPackageFileDescriptor) => {
        _downloadFile(file);
    };

    const onFileTypeChanged = (file: IDataPackageFileDescriptor, newFileType: string) => {
        _changeFileType(file, newFileType);
    };

    const onFileUploadStart = (uploaderId: string, fileName: string, sizeInKb: number) => {
        const preExistingFile = filesRef.current.find((f) => f.name === fileName);
        const sanitizedFileName = sanitize(fileName, { replacement: '_' });

        // create a new file descriptor to add to the local array
        let fileDescriptor: IDataPackageFileDescriptor = null;

        const baseProps = {
            percentUploaded: 0,
            isComplete: false,
            isError: false,
            uploadedByUserEmail: user.email,
            uploadedByUserId: user.userId,
            uploadedByUserName: user.name,
            sizeInKb,
            id: uuidv4(),
            uploaderId
        };

        if (!preExistingFile) {
            fileDescriptor = {
                name: sanitizedFileName,
                type: 'Unknown', // default to unknown file type
                ...baseProps,
                ...additionalFilePropsRef.current // add any additional props that were set by the caller
            };
            // update the local files with the newly added file prior to sending to the server
            // if it errors out it will be zeroed out progress and noted as errored
            _setFiles([...(filesRef.current || []), fileDescriptor]);
        } else {
            fileDescriptor = {
                ...preExistingFile,
                ...baseProps
            };

            const index: number = filesRef.current.findIndex((item) => item.name === fileName);
            if (index >= 0) {
                filesRef.current[index] = fileDescriptor;
            }
            // update the local files with the newly added file prior to sending to the server
            // if it errors out it will be zeroed out progress and noted as errored
            _setFiles(filesRef.current);
        }

        // update the data package with the new file descriptor
        _dataPackageService.addUpdateFile(dataPackageRef.current.id, fileDescriptor).subscribe({
            next: (dataPackage) => {
                // update the data package with latest from the server
                setDataPackage(dataPackage);

                // fire the file added event to bubble it up if needed
                onFileAdded && onFileAdded(fileDescriptor);
            },
            error: (error) => {
                _handleError('Error adding file.', error);
            }
        });
    };

    const onFileUploadCompleted = (id: string, name: string) => {
        let updatedFile: IDataPackageFileDescriptor = filesRef.current.find((f) => f.uploaderId === id);
        if (updatedFile) {
            updatedFile = {
                ...updatedFile,
                percentUploaded: 100,
                isComplete: true
            };
            _serverFileUpdate(updatedFile, `File upload not completed for file ${name ?? 'N/A'}.`);
        } else {
            //just in case, should not happen
            _modalService.error(`File upload completed, but the Completed flag not set, file: ${name}, uploader id ${id}`);
        }
    };

    const onFileUploadError = (id: string, name: string, error: string) => {
        const badFileError = `One or more files you attempted to upload to this export folder are prohibited under current export policies.`;
        const existingFile = filesRef.current.find((f) => f.uploaderId === id);
        if (!existingFile) {
            return;
        }

        const updatedFile: IDataPackageFileDescriptor = {
            ...existingFile,
            percentUploaded: 0,
            isError: true
        };

        _updateFile(updatedFile);

        //for now, limit error display to the one case of
        // bad file types in results requests
        if (error.includes(badFileError)) {
            _modalService.error(String(error));
        }

        // this was a nice idea but it isn't working as intended, too much timing
        // variability. Leaving commented out for now.
        // // serialize deletes to avoid race conditions by chaining on a queue stored on the function
        // const deleteFn = _deleteFile as any;
        // if (!deleteFn.__queue) {
        //     deleteFn.__queue = Promise.resolve();
        // }
        // deleteFn.__queue = deleteFn.__queue
        //     .then(() => _deleteFile(updatedFile))
        //     // small delay after each delete to further reduce race windows (adjust ms as needed)
        //     .then(() => new Promise((res) => setTimeout(res, 250)))
        //     .then(() => _toastService.error(`File not uploaded: ${name}: ${error}`))
        //     .catch((err) => {
        //         LoggerService.log(err);
        //     });
    };

    const onFileUploadProgress = (id: string, name: string, percentUploaded: number) => {
        const existingFile = filesRef.current.find((f) => f.uploaderId === id);
        if (!existingFile) {
            return;
        }

        const updatedFile: IDataPackageFileDescriptor = {
            ...existingFile,
            percentUploaded,
            isComplete: percentUploaded === 100
        };

        _updateFile(updatedFile);
    };

    const onAllFilesCompleted = (succeededFileIds: string[], failedFileIds: string[]) => {
        LoggerService.log(`All files completed. Succeeded: ${succeededFileIds.length}, Failed: ${failedFileIds.length}`);
        _updateFileLimitReached();
    };

    const onFileValidation = (name: string) => {
        // validate number to check if this is over the limit
        if (fileLimit && filesRefCount.current + 1 > fileLimit) {
            // file was over the limit, pop a warning
            _toastService.error(`You have reached the maximum number of files allowed (${fileLimit}).`);

            // deny file upload by returning false
            return false;
        }

        // validate the file extension
        const parts = name.split('.');
        const fileExtension = ('.' + name.split('.')[parts.length - 1]).toLowerCase();

        const isAllowedExtension = fileExtensionsRef.current?.some((file: IFileExtension) => {
            return fileExtension === file.extension.toLowerCase() && file.allowDocumentUpload;
        });

        if (fileExtensionsRef.current && fileExtensionsRef.current.length > 0 && !isAllowedExtension) {
            _toastService.error(
                `File “${name}” was rejected because this file type is not allowed on the Vivli Platform. Please contact Vivli support if you believe this file type should be allowed.`,
                { autoClose: 10000, hideProgressBar: false, closeOnClick: true }
            );
            LoggerService.debug(`File ${name} rejected due to disallowed extension: ${fileExtension}`);
            return false;
        }

        // as early as possible increment the file count
        filesRefCount.current += 1;

        // retrieve pre-existing file if available
        const preExistingFile = filesRef.current?.find((f) => f.name === name);

        // if we have a pre-existing file, we need to validate it, otherwise we can just use the file from the local array
        // this is due to a situation where there are multiple organizations uploading files to the same data package
        if (onPreExistingFile && preExistingFile) {
            //BACK OUT THAT INCREMENT IF THE FILE ALREADY EXISTS!!
            //Whether we overwrite or not, it's not another new file being added
            filesRefCount.current -= 1;
            return onPreExistingFile(name);
        }

        if (onFileExtensionWarning && showFileWarning) {
            const parts = name.split('.');
            const fileExtension = '.' + name.split('.')[parts.length - 1];

            const fileTypesToWarnFor = fileExtensionsRef.current?.filter((file: IFileExtension) => {
                return file.warnForDocumentUpload === true;
            });

            const warnForFileType = fileTypesToWarnFor?.some((file: IFileExtension) => {
                return fileExtension === file.extension;
            });

            if (warnForFileType) {
                return onFileExtensionWarning();
            }
        }

        // no validations ran, return file is valid
        return new Promise<boolean>((res) => res(true));
    };

    const onPubliclyAvailableChanged = (file: IDataPackageFileDescriptor, isPubliclyAvailable: boolean) => {
        handlePubliclyAvailableChanged(file, isPubliclyAvailable);
    };

    const getDataPackage = () => {
        setIsLoading(true);

        _dataPackageService
            .getDataPackage(dataPackageId)
            .pipe(first())
            .subscribe({
                next: (dp) => {
                    setDataPackage(dp);
                    _setFiles(dp.dataPackageFileDescriptors);

                    // set initial file count
                    filesRefCount.current = dp.dataPackageFileDescriptors.length;
                },
                error: (error) => {
                    _handleError('Cannot get the Data Package', error);

                    // set data package to null so that loading states work properly
                    setDataPackage(null);
                },
                complete: () => setIsLoading(false)
            });
    };

    const _sortFiles = (filesToSort: IDataPackageFileDescriptor[]) => {
        let sortedFiles = filesToSort;
        if (sortFilesByDate) {
            // sort by last updated date
            sortedFiles = filesToSort?.sort((a, b) => moment(b.updatedDateTime).valueOf() - moment(a.updatedDateTime).valueOf());
        }

        return sortedFiles;
    };

    const getSortedFiles = (files: IDataPackageFileDescriptor[]) => {
        if (secondaryFilesToShow) {
            const distinctFiles = secondaryFilesToShow.concat(
                (files || []).filter((item2) => !secondaryFilesToShow.some((item1) => item1.name === item2.name))
            );
            return _sortFiles(distinctFiles);
        }

        return _sortFiles(files);
    };

    const sortedFiles = useMemo(() => getSortedFiles(files), [files, secondaryFilesToShow]);

    useEffect(() => {
        if (!dataPackageId) {
            return;
        }

        getDataPackage();
    }, [dataPackageId]);

    useEffect(() => {
        if (!publiclyAvailableOrgId || !readOnly) {
            return;
        }

        _getPubliclyAvailableOrg();
    }, [publiclyAvailableOrgId, readOnly]);

    useEffect(() => {
        if (!dataPackage) {
            return;
        }

        _validateRequiredTypes();

        // only fire on ready function once
        if (!initializedRef.current) {
            initializedRef.current = true;
            onDataPackageReady && onDataPackageReady(dataPackage);
        }
    }, [dataPackage]);

    useEffect(() => {
        // no server side update completed, don't fire an update
        if (!overrideDataPackage || overrideDataPackage._etag === dataPackage._etag) return;

        setDataPackage(overrideDataPackage);
        _setFiles(overrideDataPackage.dataPackageFileDescriptors);
    }, [overrideDataPackage]);

    useEffect(() => {
        // use a stateless ref due to a closure issue with fine uploader
        if (!config) {
            return;
        }
        fileExtensionsRef.current = config.fileExtensions;
    }, [config]);

    return {
        dataPackage,
        files: sortedFiles,
        publiclyAvailableOrg,
        isLoading,
        hasRequiredTypes,
        fileLimitReached,
        getDataPackage,
        setDataPackage,
        validateUploadedData,
        setAdditionalFileProps,
        onPreExistingFile,
        onFileDeleted,
        onFileDownload,
        onFileTypeChanged,
        onFileUploadCompleted,
        onFileUploadError,
        onFileUploadStart,
        onFileUploadProgress,
        onAllFilesCompleted,
        onFileValidation,
        onPubliclyAvailableChanged
    };
};
